import React from 'react'
import Layout from '@components/Layout'
import { graphql } from 'gatsby'
import NewsDetailContent from '@sections/NewsDetailContent'
import { getHrefLangUrls, handleSEO } from '@utils'
import TitleAndMetaTags from '@components/TitleAndMetaTags'
import { useGtag, useLocale } from '@hooks'

const NewsDetail = ({ data, pageContext }) => {
  const { getLocalizedSlug } = useLocale()

  useGtag({
    action: 'News',
    categories: data?.news?.associatedPracticeAreas?.map(({ title }) => title),
    label: getLocalizedSlug({
      slug: data?.news?.slug,
      contentType: data?.news?.__typename,
    }),
  })

  return (
    <Layout {...pageContext} contentType="news">
      <NewsDetailContent {...data} next={pageContext?.nextEntries} />
    </Layout>
  )
}

export default NewsDetail

export const query = graphql`
  query newsDetailV2(
    $contentful_id: String!
    $node_locale: String!
    $displayOnSitesAsNodeLocales: [String!]
  ) {
    parentPage: contentfulPage(
      contentful_id: { eq: "43jJTUzlBaamDwaKfJcbZf" }
      node_locale: { eq: $node_locale }
    ) {
      ...basePageFields
      parentId: contentful_id
    }
    news: contentfulNews(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $node_locale }
    ) {
      ...baseNewsFields
      associatedPracticeAreas {
        ...basePracticeAreaFields
      }
      associatedProfessionals {
        ...baseProfessionalFields
        offices {
          ...baseOfficeFields
        }
        image {
          ...assetBase
          # gatsbyImageData(width: 173)
        }
      }
      # featuredImage {
      #   ...assetBase
      #   # gatsbyImageData(
      #   #   layout: FULL_WIDTH
      #   #   height: 385
      #   #   placeholder: BLURRED
      #   #   resizingBehavior: FILL
      #   # )
      # }
      contentHtml: childContentfulNewsContentHtmlJsonNode {
        useHTMLField
        html
      }
      content {
        raw
        references {
          ...assetBase
          ...articleLinkFields
          ...articleCategoryLinkFields
          ...eventLinkFields
          ...filterPageLinkFields
          ...newsLinkFields
          ...officeLinkFields
          ...pageLinkFields
          ...practiceAreaLinkFields
          ...professionalLinkFields
          ...moduleAccordion
          ...moduleMediaEmbed
          ...moduleList
        }
      }
      seo {
        ...seoMetadata
      }
    }
    currentPageWithAllLocales: allContentfulNews(
      filter: {
        node_locale: { in: $displayOnSitesAsNodeLocales }
        contentful_id: { eq: $contentful_id }
      }
    ) {
      nodes {
        slug
        node_locale
      }
    }
  }
`

export const Head = ({
  data: { news, currentPageWithAllLocales },
  pageContext: { displayLocale, parentPages, localizedSlug },
  path,
}) => {
  const { title, content, contentHtml, associatedPracticeAreas, seo } = news

  const { seoTitle, seoDescription, seoKeywords, seoImage } = handleSEO(
    seo,
    {
      title: title,
      descriptionHtml: contentHtml,
      content: content,
      practiceAreas: associatedPracticeAreas,
    },
    displayLocale,
    'news'
  )

  const hrefLangUrls = getHrefLangUrls(
    currentPageWithAllLocales?.nodes,
    parentPages
  )

  return (
    <TitleAndMetaTags
      hrefLangUrls={hrefLangUrls}
      title={seoTitle}
      description={seoDescription}
      keywords={seoKeywords}
      image={seoImage}
      canonicalSlug={localizedSlug}
      usDefault={seo?.enUsCanonicalUrl}
      noIndex={seo?.noIndex}
      noArchive={seo?.noArchive}
    />
  )
}
